// NPM
import React, { useEffect, Fragment, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _, { isNil, omitBy, isEmpty, map, filter, cloneDeep, flatten } from 'lodash';
import moment from 'moment';

// COMPONENT
import CancelForm from './cancel-order-form';

// ANT COMPONENT
import { Space, Table, Row, Col, Checkbox, Alert } from 'antd';
import {
  Button,
  SkeletonLoader,
  BasicModal,
  Loader,
  ActionMenu,
  DataTable,
  CheckboxGroup,
} from '@life-line-lab/components-react';
import Icon, {
  SyncOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';

// STYLE
import './purchase-list.scss';

// HELPER
import { encode } from "Helpers/enc";
import useSession from 'Hooks/session.hook';

interface ProductCartProps {
  ordersList: any,
  status: any
  cancelOrder: ({ }: any) => void;
  getReceipt: ({ }: any) => void;
  getPaymentStatus: ({ }: any) => void;
  paymentStatus: any;
  handleCloseOrder: ({ }: any) => void;
}

export type OrderStatusTypes = "OPEN" | "DISPATCH_INPROGRESS" | "CLOSED" | "CANCELLED" | "REJECTED" | "APPROVED" | "REFUNDED" | "PENDING_PAYMENT"
export type PaymentStatusTypes = "PAID" | "NOT_PAID" | "PENDING" | "FAILED" | "ABORTED" | "INVALID" | "NOT_FOUND" | "REFUND_INPROGRESS" | "REFUNDED"
export type InvalidVoucherStatusType = 'FULLY_REDEEMED' | 'EXPIRED';

const ProductPurchaseInfo: React.FC<ProductCartProps> = ({ ordersList, status, cancelOrder, getReceipt, getPaymentStatus, paymentStatus, handleCloseOrder }) => {
  const { t } = useTranslation()
  const history = useHistory();
  const { profile } = useSession();

  const onProductClick = (inventory) => {
    const productDisplaySites = inventory.displaySites ?? []
    if (!productDisplaySites.includes(process.env.ACCOUNT_TYPE)) return;
    const variantAllDisplaySites = _.flatten(inventory.product?.productVariants?.map(pv => pv.displaySites)) ?? []
    if (!variantAllDisplaySites.includes(process.env.ACCOUNT_TYPE)) return;

    const inventoryID = encode(inventory._id);
    history.push('/products/' + inventoryID);
  }

  const deliveryType = {
    'STANDARD': 'Standard',
    'NEXT_DAY': 'Next Day',
    'EXPRESS': 'Express'
  }

  const [orderNumber, setOrderNumber] = useState<string>('');
  const [productName, setProductName] = useState<string>('');
  const [cancelPopup, setCancelPopup] = useState<boolean>(false);
  const [receiptPopup, setReceiptPopup] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [orderID, setOrderID] = useState("");

  const [cancelOrderObj, setCancelOrderObj] = useState<any>({})
  const [receiptObj, setReceiptObj] = useState<any>({})


  const onCloseModal = () => {
    setCancelPopup(false)
    setReceiptPopup(false)
    setIsOpen(false);
    setOrderID("");
  }

  const onCancel = (obj: { orderCode: string, items_data: object[], [x: string]: any }) => {
    const productName = (obj.items_data.map((o: any) => o.product.name) as string[]).join(', ');

    setProductName(productName);
    setOrderNumber(obj.orderCode);
    setCancelPopup(true);
    setCancelOrderObj(obj)
  }
  const confirmCancel = () => {
    setCancelPopup(false)

    let cancelObj = {
      orderID: cancelOrderObj._id,
      newStatus: 'CANCELLED'
    }
    cancelOrder(cancelObj)
  }

  const onReceipt = (obj) => {
    setReceiptPopup(true);
    getReceipt({
      "orderID": obj._id
    })
  }

  const onRefreshPayment = (obj) => {
    getPaymentStatus({
      "paymentID": obj.payment_data._id,
      "orderID": obj._id
    })
  }

  const downloadReceipt = () => {
    window.open(receiptObj.file_url)
    onCloseModal();
  }

  useEffect(() => {
    if (status.receipt == 'failed') {
      onCloseModal()
    }
    if (status.receipt == 'success') {
      setReceiptObj(status.data)
    }
  }, [status])

  useEffect(() => {
    if (!profile) {
      history.push('/login')
    }
  }, [profile])

  const goToTracking = (orderObj) => {
    const orderID = encode(orderObj._id);
    history.push('/purchase/' + orderID);
  }

  const canShowCancelOrderBtnForPaidOrder = (orderStatus: OrderStatusTypes, paymentStatus: PaymentStatusTypes, orderData: any) => {
    const allowedOrderStatus: OrderStatusTypes[] = [
      'OPEN',
      'APPROVED',
    ]

    const allowedPaymentStatus: PaymentStatusTypes[] = [
      'PAID',
    ]

    // there are redeemed voucher(s) under this order
    const someVouchersAreRedeemed = !isEmpty(flatten(orderData.items?.map(oi => oi.vouchers).
                                      filter(v => !isNil(v))).
                                      filter((v: any) => v.status === 'REDEEMED'));

    if (someVouchersAreRedeemed) return false;

    return allowedOrderStatus.includes(orderStatus) &&
      allowedPaymentStatus.includes(paymentStatus)
  }

  const canShowTrackOrderBtn = (orderStatus: OrderStatusTypes, paymentStatus: PaymentStatusTypes) => {
    const allowedOrderStatus: OrderStatusTypes[] = [
      'OPEN',
      'APPROVED',
      'DISPATCH_INPROGRESS',
    ]

    const allowedPaymentStatus: PaymentStatusTypes[] = [
      'PAID',
    ]

    return allowedOrderStatus.includes(orderStatus) &&
      allowedPaymentStatus.includes(paymentStatus)
  }

  const getGenericOrderStatusComponent = (orderStatus: OrderStatusTypes) => {
    const orderStatusDisplayText = orderStatus === 'CLOSED' ?
      'COMPLETED' :
      orderStatus

    return (
      <Col lg={24} md={24} sm={24} xs={12} className="py-5 px-5">
        <div className={`status ${orderStatus}`}>
          <strong>{orderStatusDisplayText}</strong>
        </div>
      </Col>
    )
  }
  const closeOrder = () => {
    handleCloseOrder({
      orderID
    });
    onCloseModal();
  }

  const goToVoucherListPage = (orderID) => {
    history.push(`/voucher-listing/${encode(orderID)}`)
  }

  return (
    <div className="product purchase-list">

      {status.fetch == 'loading' &&
        <>
          <SkeletonLoader type="default" />
          <SkeletonLoader type="default" />
          <SkeletonLoader type="default" />
        </>
      }

      <BasicModal title="Receipt" isClosable={true} isMaskClosable={true} isOpen={receiptPopup} onClose={onCloseModal}>
        <div className="receipt-modal">
          <div>
            {status.receipt == 'loading' ?

              <div className="main-title">
                <SyncOutlined spin />
                <div>{t('txt.generatingReceipt')}</div>
              </div>
              :
              <>
                <div className="main-title"> {t('txt.downloadYourReceiptHere')}</div>
                <Button
                  className="btn-buy"
                  type="primary"
                  label={
                    <React.Fragment>
                      <span>{t('action.downloadReceipt')}</span> <DownloadOutlined />
                    </React.Fragment>
                  }
                  size="large"
                  onClick={() => downloadReceipt()}
                />



              </>}

            {/* <div>
                            <img src={receiptObj.file_url} />
                            <iframe src={receiptObj.s3Path}></iframe>
                        </div> */}
          </div>


        </div>
      </BasicModal>


      <BasicModal title={t('title.cancelOrder')} isClosable={true} isMaskClosable={true} isOpen={cancelPopup} onClose={onCloseModal}>
        <div className="cancel-modal">
          <Space direction="vertical" size="middle">
            <h2 className="main-title">{t('txt.cancelOrderConfirmation')}</h2>
            <Alert
              style={{ textAlign: 'left' }}
              type="info"
              message={(
                <Space direction="vertical" size="small">
                  <span className="text-underline text-blue-300">{t('txt.refundNotice')}</span>
                  <div className="text-blue-300" dangerouslySetInnerHTML={{ __html: t('txt.cancelOrderConfirmationDesc') }} />
                  <span className="text-blue-300">{t('txt.wishToContinue')}</span>
                </Space>
              )}
            />
          </Space>
          <CancelForm
            productName={productName}
            orderNumber={orderNumber}
            cancelStatus={status}
            onCloseModal={onCloseModal}
            confirmCancel={confirmCancel}
          />
        </div>
      </BasicModal>

      <Row gutter={16}>
        <Col lg={24} md={24} sm={24} xs={24}>
          {ordersList.length && status.fetch !== 'loading' ?
            <Alert
              type="info"
              showIcon
              message={(
                <Fragment>
                  <span>{t('txt.orderQueries')}</span>
                  <Link
                    style={{ marginRight: '3px', marginLeft: '3px', color: '#B42C30' }}
                    to="/support"
                  >
                    {t("txt.contactSupport")}
                  </Link>
                  <span> {t('txt.forAssistance')}</span>
                </Fragment>
              )}
            />
            : ''}
        </Col>
        {ordersList.length && status.fetch !== 'loading' ? ordersList.map((o, i) => {
          return (
            <div className="shopping-details my-20">
              <Col lg={24} md={24} sm={24} xs={24}>

                <div className="cart-table">
                  <div>
                    <div className="purchase-section1">
                      <Row gutter={16} className="rrow">
                        <Col lg={1} md={1} sm={1} xs={0} className="ccol"></Col>

                        <Col lg={4} md={4} sm={8} xs={12} className="ccol flex-align-center">
                          <div className="title">#{o.orderCode}</div>
                        </Col>

                        {o.purchaseType != "VOUCHER" && o.purchaseType !== 'NO_SHIPPING' ? <Col lg={4} md={4} sm={8} xs={12} className="ccol">
                          <div className="title">{t('label.shippingTo')}</div>
                          <div className="val">
                            <strong>{o.receiver?.fullName}</strong>
                          </div>
                        </Col> : null}
                        <Col lg={4} md={4} sm={8} xs={12} className="ccol">
                          <div className="title">{t('label.orderPaymentStatus')}</div>
                          <h4 className="pay-sts color-secondary">
                            <strong>
                              {o.payment_data?.status && o.payment_data?.status != 'NOT_FOUND' ? o.payment_data?.status : t('txt.UNPAID')}
                            </strong>
                            <div>
                              {o.payment_data?.receipt && <a className="view-receipt" onClick={() => { onReceipt(o) }}>{t('action.viewReceipt')}</a>}
                            </div>
                            <h5>
                              {o.payment_data?.status && o.payment_data?.status == 'PENDING' && paymentStatus != 'loading' ? <a className="view-receipt" onClick={() => { onRefreshPayment(o) }}>{t('action.refreshStatus')}</a> : null}
                              {o.payment_data?.status && o.payment_data?.status == 'PENDING' && paymentStatus == 'loading' && <span>Fetching...</span>}
                            </h5>
                          </h4>
                        </Col>
                        {o.purchaseType != "VOUCHER" && o.purchaseType !== 'NO_SHIPPING' ? <>
                          <Col lg={4} md={4} sm={8} xs={12} className="ccol">
                            <div className="title">{t('label.latestDeliveryStatus')}</div>
                            {o.payment_data?.status == 'PAID' ? <>
                              {o?.status != 'CANCELLED' && o?.status != 'REJECTED' && o?.status != 'REFUNDED' ? <h4 className="val color-secondary"><strong>{o?.latestDispatchUpdate?.status || t('txt.orderProcessing')}</strong></h4> : '-'}
                              {o?.latestDispatchUpdate?.timestamp &&
                                <div className="font-12">
                                  <strong>{moment(o?.latestDispatchUpdate?.timestamp).format('DD-MM-YYYY hh:mm a')}</strong>
                                </div>
                              }
                            </> : <h4>-</h4>}
                          </Col>
                          <Col lg={4} md={4} sm={8} xs={12} className="ccol">
                            <div className="title">{t('label.deliveryOption')}</div>
                            {o.payment_data?.status == 'PAID' && (o?.status != 'CANCELLED' && o?.status != 'REJECTED' && o?.status != 'REFUNDED') ? <>
                              <h4 className="val color-secondary"><strong>{deliveryType[o.deliveryInfo?.deliveryType]} {t('txt.Delivery')}</strong></h4>

                              {o.status === 'OPEN' || o.status === 'APPROVED' || o.status === 'DISPATCH_INPROGRESS' ?
                                <div className="font-12">
                                  {o?.orderDate && <strong> {t('label.receiveBy')} {moment(o?.orderDate).add(4, 'days').format('DD MMM')} - {moment(o?.orderDate).add(11, 'days').format('DD MMM')}</strong>}
                                </div>
                                : null
                              }
                            </> : <h4>-</h4>}
                          </Col></> : null}
                      </Row>

                      <Row gutter={16} style={{ borderBottom: '1px solid #ddd', borderRadius: '6px' }}>
                        <Col lg={20} md={20} sm={20} xs={24} className="ccol">
                          {o.items && o.items.map((record, j) => {
                            let itemImage = record.product.product.images?.[0]?.file_data?.file_url || '/assets/images/product-placeholder.png';
                            const variantCode = record.variantCode;
                            if (variantCode && record.product.product?.images?.length) {
                              const matchedImageLink = record.product.product?.images.find(elem => elem.variantCode === variantCode);
                              if (matchedImageLink) {
                                itemImage = matchedImageLink.file_data?.file_url || itemImage;
                              }
                            }

                            return (
                              <Row gutter={16} className={"image-section " + (o.status == 'OPEN' ? '' : '')}>
                                <Col lg={1} md={1} sm={1} xs={0} className="ccol"></Col>
                                <Col lg={3} md={3} sm={3} xs={6} className="ccol flex-align-center">
                                  <img
                                    alt={t('alt.logo')}
                                    src={itemImage}
                                    style={{ width: '100%' }}
                                  /> &nbsp;
                                </Col>
                                <Col lg={12} md={12} sm={12} xs={12} className="ccol info">
                                  <div onClick={() => onProductClick(record.product)} style={{ textTransform: 'capitalize', fontSize: '16px' }}> <strong>{record.product.product.name}</strong></div>
                                  <h5 className="color-secondary"><strong>({record.variant}) x {record.quantity}</strong></h5>
                                  <h4 className="color-secondary"><strong>RM {parseFloat(record.unitPrice).toFixed(2)}</strong></h4>
                                </Col>
                                {/* do not show voucher list for business account */}
                                {record?.vouchers && !profile.isBusiness ?
                                  <Col lg={6} md={6} sm={24} xs={24} className="ccol m-auto">
                                    {record?.vouchers?.map((voucher, j) => {
                                      return (
                                        <Col lg={24} md={24} sm={24} xs={24} className="ccol">
                                          <div className="coupon-voucher">
                                            <div className="inner">
                                              <div className="left-circle"></div>
                                              <h5 className="code"><strong>{voucher.code}</strong></h5>

                                              {voucher.status == 'ACTIVE' ? <h5 className="validity"><strong>Valid till {moment(voucher.expiresAt).format('DD-MM-YYYY')}</strong></h5>
                                                :
                                                <h5 className={"validity -" + voucher.status}><strong>{voucher.status}</strong></h5>}

                                              <div className="right-circle"></div>
                                            </div>
                                          </div>
                                        </Col>
                                      )
                                    })}
                                  </Col> : null}
                              </Row>
                            )
                          })}
                        </Col>
                        <Col lg={4} md={4} sm={4} xs={24} className="canceBtn">

                          <Row>
                            {
                              o.purchaseType != "VOUCHER" && o.purchaseType !== 'NO_SHIPPING' &&
                              canShowTrackOrderBtn(o.status, o.payment_data?.status) &&
                              <Col lg={24} md={24} sm={24} xs={12} className="py-5 px-5">
                                <Button
                                  className="btn-cart"
                                  type="primary"
                                  style={{ width: '100%' }}
                                  label={t('action.trackOrder')}
                                  size="large"
                                  onClick={() => {
                                    goToTracking(o)
                                  }}
                                />
                              </Col>
                            }
                            {
                              o.status !== 'CANCELLED' &&
                              !isEmpty(o.items?.filter(orderItem => !isEmpty(orderItem.vouchers))) &&
                              profile.isBusiness && 
                              <Col lg={24} md={24} sm={24} xs={12} className="py-5 px-5">
                                <Button
                                  className="btn-cart btn-view-vouchers"
                                  type="primary"
                                  style={{ width: '100%' }}
                                  label={t('action.viewVoucherList')}
                                  size="large"
                                  onClick={() => {
                                    goToVoucherListPage(o._id)
                                  }}
                                />
                              </Col>
                            }
                            {
                              o.purchaseType != "VOUCHER" && o.purchaseType !== 'NO_SHIPPING' &&
                              canShowCancelOrderBtnForPaidOrder(o.status, o.payment_data?.status, o) &&
                              <Col lg={24} md={24} sm={24} xs={12} className="py-5 px-5">
                                <Button
                                  className="btn-cart"
                                  type="secondary"
                                  style={{ width: '100%' }}
                                  label={t('action.cancelOrder')}
                                  size="large"
                                  onClick={() => {
                                    onCancel(o)
                                  }}
                                />
                              </Col>
                            }
                            {
                              !(o.purchaseType != "VOUCHER" && o.purchaseType !== 'NO_SHIPPING') &&
                              o.allowVoucherCancellation && canShowCancelOrderBtnForPaidOrder(o.status, o.payment_data?.status, o) &&

                              <Col lg={24} md={24} sm={24} xs={12} className="py-5 px-5">
                                <Button
                                  className="btn-cart"
                                  type="secondary"
                                  style={{ width: '100%' }}
                                  label={t('action.cancelOrder')}
                                  size="large"
                                  onClick={() => {
                                    onCancel(o)
                                  }}
                                />
                              </Col>
                            }

                            {
                              [
                                'CANCELLED',
                                'REJECTED',
                                'REFUNDED',
                                'CLOSED',
                              ].includes(o.status) &&
                              getGenericOrderStatusComponent(o.status as OrderStatusTypes)
                            }

                            {
                              o.status === 'DELIVERED' && <Col lg={24} md={24} sm={24} xs={12} className="py-5 px-5">
                                <Button
                                  className="btn-cart"
                                  type="secondary"
                                  style={{ width: '100%' }}
                                  label={t('action.orderReceived')}
                                  size="large"
                                  onClick={() => {
                                    setIsOpen(true);
                                    setOrderID(o._id);
                                  }}
                                />
                              </Col>
                            }

                            {/* <Col lg={24} md={24} sm={24} xs={12} className="py-5 px-5">
                              {o.status === 'OPEN' && o.payment_data?.status == 'PAID' &&
                                <Button
                                  style={{ width: '100%' }}
                                  className="btn-cart"
                                  type="secondary"
                                  label={t('action.cancelOrder')}
                                  size="large"
                                  onClick={() => {
                                    onCancel(o)
                                  }}
                                />
                              }
                              {
                                o.status === 'CANCELLED' && <div className="color-danger">
                                  <strong>CANCELLED</strong>
                                </div>
                              }
                              {
                                o.status === 'REJECTED' && <div className="color-danger">
                                  <strong>REJECTED</strong>
                                </div>
                              }
                              {
                                o.status === 'CLOSED' && <div className="color-success">
                                  <strong>COMPLETED</strong>
                                </div>
                              }
                            </Col> */}
                          </Row>
                        </Col>
                      </Row>
                      <div>

                        <Row gutter={16} className="order-total">
                          <Col span={12} xs={12}> {t('txt.soldby')} : {o.supplier?.name} </Col>
                          <Col span={12} xs={12} className="text-end"> {t('txt.orderTotal')} : RM{parseFloat(o.paymentDetails?.total).toFixed(2)}</Col></Row>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </div>
          )
        }) :

          status.fetch == 'success' ? <div className="my-10 m-auto justify-center">
            <div>
              <img
                className="mx-25"
                alt={t('alt.logo')}
                src='/assets/images/empty-order.png'
                width={150}
              />
              <h3 className="color-primary"><strong>{t('txt.noPurchaseHistoryFound')}</strong></h3>
            </div>
          </div> : null}
      </Row>

      <BasicModal title={t("label.confirmOrderReceive")} isClosable={true} isMaskClosable={true}
        isOpen={isOpen} onClose={onCloseModal}>
        <Space
          direction="vertical"
          size="large"
          className="confirmation-modal scroll-x"
          align="center"
        >
          {t("txt.confirmOrderReceive")}
          <Space
            direction="horizontal"
            size="large"
            className="confirm-button-space"
            style={{ justifyContent: "center" }}
            align="center"
          >
            <Button type="secondary" size="large" label={t("action.cancel")} onClick={onCloseModal} />
            <Button type="primary" size="large" label={t("action.confirm")} onClick={closeOrder} />
          </Space>
        </Space>
      </BasicModal>
    </div >
  )
}


export default ProductPurchaseInfo;
